/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * useCurrentUserDivision Hook
 *
 * Returns the current user's primary division (or first assigned division).
 * Used to provide division-contextual pricing on costs pages.
 */

import { useAuthSafe } from "@/hooks/auth/use-auth-safe";
import { useOrganizationQueryGuard } from "@/hooks/use-organization-query";
import { createApiClient, isApiFailure } from "@/lib/api/api-client";
import { QUERY_GC_TIME, QUERY_STALE_TIME } from "@/lib/constants/query-config";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

interface UserDivisionAssignment {
  id: string;
  division_id: string;
  is_primary: boolean | null;
  role_in_division: string | null;
  assigned_at: string;
}

interface MemberDivisionsResponse {
  memberId: string;
  divisions: UserDivisionAssignment[];
  divisionIds: string[];
}

export function useCurrentUserDivision() {
  const { userId, getToken } = useAuthSafe();
  const { organizationId, queryEnabled } = useOrganizationQueryGuard({
    enabled: !!userId,
    debugKey: "useCurrentUserDivision",
  });

  const api = useMemo(
    () =>
      createApiClient({
        getToken,
        getOrganizationId: () => organizationId ?? undefined,
        autoRetry429: true,
      }),
    [getToken, organizationId],
  );

  const query = useQuery<MemberDivisionsResponse>({
    queryKey: ["current-user-divisions", organizationId, userId],
    enabled: queryEnabled && !!userId,
    staleTime: QUERY_STALE_TIME.VERY_STABLE,
    gcTime: QUERY_GC_TIME.STANDARD,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const res = await api.get<MemberDivisionsResponse>(
        `/api/organizations/members/${userId}/divisions`,
      );
      if (isApiFailure(res)) {
        throw new Error(res.message);
      }
      return res.data!;
    },
  });

  const primaryDivisionId = useMemo(() => {
    const divisions = query.data?.divisions;
    if (!divisions || divisions.length === 0) return null;
    const primary = divisions.find((d) => d.is_primary);
    return primary?.division_id ?? divisions[0]!.division_id;
  }, [query.data]);

  return {
    divisionId: primaryDivisionId,
    divisions: query.data?.divisions ?? [],
    isLoading: query.isLoading,
  };
}
